<template>
  <b-form inline class="image__form mb-2">
    <div class="preview" @click="showFileBrowser">
      <input
        type="file"
        accept="image/*"
        hidden
        ref="file"
        @change="onFileChange"
        v-if="!reportFinalized"
      />
      <img :src="previewUrl" v-if="previewUrl" />
      <v-icon name="plus" scale="2" v-else />
    </div>
    <canvas ref="canvas" style="display: none"></canvas>
    <b-form-input
      class="comment mb-2 mx-2 mb-sm-0"
      v-model="comment"
      :disabled="reportFinalized"
      :readonly="reportFinalized"
      :placeholder="$t('smartChart.commentForImage')"
    ></b-form-input>
    <div v-if="!reportFinalized">
      <b-button variant="primary" @click="uploadImage" v-if="!imagePk">{{
        $t('general.save')
      }}</b-button>
      <b-button variant="primary" @click="updateImage" v-else>{{
        $t('general.update')
      }}</b-button>
      <b-button
        variant="primary"
        @click="removeImage"
        v-if="imagePk"
        class="ml-2"
        >{{ $t('general.remove') }}</b-button
      >
    </div>
    <b-modal v-model="showLarge" ok-only>
      <div class="large__preview">
        <img :src="previewUrl" v-if="previewUrl" />
        <p v-else>no image provided</p>
        <p>{{ comment }}</p>
      </div>
    </b-modal>
  </b-form>
</template>

<script>
/* eslint-disable comma-dangle */
import reportService from '@/services/chart-report.service';

export default {
  name: 'UploadImageInlineForm',
  props: {
    defaultComment: {
      type: String,
      default: '',
    },
    reportType: {
      type: String,
    },
    reportPk: {
      type: String,
    },
    imagePk: {
      type: String,
    },
    imageURL: {
      type: String,
    },
    reportFinalized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      file: null,
      previewUrl: this.imageURL,
      comment: this.defaultComment,
      showLarge: false,
      resizedImage: '',
      resizedIamgeBlob: null,
    };
  },
  methods: {
    showFileBrowser() {
      if (this.reportFinalized) {
        this.showLarge = true;
        return;
      }
      this.$refs.file.click();
    },
    clearForm() {
      this.file = null;
      this.previewUrl = '';
      this.comment = '';
      this.$refs.file.value = '';
      this.resizedImage = '';
      this.resizedIamgeBlob = null;
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) {
        console.warn('no image selected.');
        return false;
      }
      if (!file.type.match('image.*')) {
        return false;
      }
      this.file = file;
      const that = this;
      const reader = new FileReader();
      reader.onload = function cb(es) {
        const image = new Image();
        image.onload = async () => {
          const resizedImage = that.resizeAndCropImage(image);
          that.resizedImage = resizedImage;
          that.previewUrl = resizedImage;
          that.resizedIamgeBlob = await that.urlToBlob(resizedImage);
        };
        image.src = es.target.result;
      };
      reader.readAsDataURL(file);
      return true;
    },

    async removeImage() {
      if (this.reportFinalized) {
        this.flash(this.$t('smartChart.reportFinalizedError'), 'error', {
          timeout: 4000,
        });
        return false;
      }
      const confirm = await this.$bvModal.msgBoxConfirm(
        this.$t('smartChart.confirmUpdateImage'),
        {
          title: this.$t('smartChart.pleaseConfirm'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('smartChart.yes'),
          cancelTitle: this.$t('smartChart.no'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        }
      );
      if (!confirm) {
        return false;
      }
      await reportService
        .deleteReportImage(
          { id: this.reportPk, imageID: this.imagePk },
          this.reportType
        )
        .then(() => {
          this.clearForm();
          this.$emit('images-updated');
        })
        // eslint-disable-next-line arrow-parens
        .catch(e => {
          console.log('report image update failed: ', e);
          this.flash(this.$t('smartChart.formImageSaveFailed'), 'error', {
            timeout: 4000,
          });
        });
      return true;
    },
    async updateImage() {
      if (this.reportFinalized) {
        this.flash(this.$t('smartChart.reportFinalizedError'), 'error', {
          timeout: 4000,
        });
        return false;
      }
      this.loading = true;
      // check if we need update file
      if (this.file) {
        const confirm = await this.$bvModal.msgBoxConfirm(
          this.$t('smartChart.confirmUpdateImage'),
          {
            title: this.$t('smartChart.pleaseConfirm'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('smartChart.yes'),
            cancelTitle: this.$t('smartChart.no'),
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (!confirm) {
          this.flash(this.$t('smartChart.updateImageCancelled'), 'warning', {
            timeout: 3000,
          });
          return false;
        }
        reportService
          .deleteReportImage(
            { id: this.reportPk, imageID: this.imagePk },
            this.reportType
          )
          .then(() => {
            this.uploadImage();
          })
          // eslint-disable-next-line arrow-parens
          .catch(e => {
            console.log('report image update failed: ', e);
            this.flash(this.$t('smartChart.formImageSaveFailed'), 'error', {
              timeout: 4000,
            });
          });
      } else {
        // update comment only
        reportService
          .updateReportImageComment(
            { id: this.reportPk, imageID: this.imagePk, comment: this.comment },
            this.reportType
          )
          .then(() => {
            this.flash(this.$t('smartChart.formImageSaved'), 'success', {
              timeout: 3000,
            });
            this.$emit('images-updated');
          })
          // eslint-disable-next-line arrow-parens
          .catch(e => {
            console.log('report image update failed: ', e);
            this.flash(this.$t('smartChart.formImageSaveFailed'), 'error', {
              timeout: 4000,
            });
          });
      }

      return true;
    },
    resizeAndCropImage(image) {
      const { canvas } = this.$refs;
      const context = canvas.getContext('2d');

      const maxSize = 100;
      let { width } = image;
      let { height } = image;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }

      canvas.width = width;
      canvas.height = height;

      context.drawImage(image, 0, 0, width, height);

      try {
        const dataUrl = canvas.toDataURL('image/png', 0.9);

        return dataUrl;
      } catch (e) {
        console.error('Image saving error.', e);
        return '';
      }
    },
    async urlToBlob(url) {
      const blob = await (await fetch(url)).blob();
      return blob;
    },
    async uploadImage(silent = false) {
      if (this.reportFinalized) {
        this.flash(this.$t('smartChart.reportFinalizedError'), 'error', {
          timeout: 4000,
        });
        return false;
      }
      if (!this.file || !this.resizedImage) {
        this.flash(this.$t('smartChart.selectFileFirstError'), 'error', {
          timeout: 4000,
        });
        return false;
      }
      if (!this.resizedIamgeBlob) {
        this.resizedIamgeBlob = await this.urlToBlob(this.resizedImage);
      }
      this.loading = true;
      const form = new FormData();
      form.append('myFile', this.resizedIamgeBlob);
      form.append('comment', this.comment);
      return (
        reportService
          .uploadReportImage(this.reportPk, form, this.reportType)
          .then(() => {
            this.file = '';
            this.loading = false;
            if (!silent) {
              this.flash(this.$t('smartChart.formImageSaved'), 'success', {
                timeout: 3000,
              });
            }
            this.clearForm();
            this.$emit('images-updated');
          })
          // eslint-disable-next-line arrow-parens
          .catch(e => {
            console.log('report image upload failed: ', e);
            this.flash(this.$t('smartChart.formImageSaveFailed'), 'error', {
              timeout: 4000,
            });
          })
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.image__form {
  justify-content: space-between;
  .preview {
    width: 38px;
    height: 38px;
    cursor: pointer;
    img,
    .fa-icon {
      width: 100%;
      height: 100%;
      vertical-align: middle;

      max-width: 100%;
      max-height: 100%;
    }
  }
  .comment {
    flex: 1;
  }
}
.large__preview {
  img {
    width: 100%;
    height: auto;

    max-width: 100%;
    max-height: 100%;
  }
}
</style>
